import { X, RotateCcw, Trash2, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/Tooltip';

interface BulkSelectionActionsProps {
  selectedCount: number;
  hasLockedItems: boolean;
  hasNonFehlerItems: boolean;
  hasErledigtItems: boolean;

  status: 'idle' | 'processing' | 'completed' | 'error';

  isBulkDeletePending: boolean;
  isBulkRetryPending: boolean;

  onClearSelection: () => void;
  onBulkRetryClick: () => void;
  onBulkDeleteClick: () => void;
  onBulkShelveClick: () => void;
}

export function BulkSelectionActions({
  selectedCount,
  hasLockedItems,
  hasNonFehlerItems,
  hasErledigtItems,
  status,
  isBulkDeletePending,
  isBulkRetryPending,
  onClearSelection,
  onBulkRetryClick,
  onBulkDeleteClick,
  onBulkShelveClick,
}: BulkSelectionActionsProps) {
  if (selectedCount === 0) return null;

  const disabled =
    status === 'processing' || isBulkDeletePending || isBulkRetryPending;

  return (
    <div className={'flex'}>
      <div
        className={`bg-white border border-table-border rounded-lg p-4 w-full ${
          hasLockedItems ? 'flex flex-col' : 'flex items-center'
        }`}
      >
        <div
          className={`w-full ${
            hasLockedItems
              ? 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3'
              : 'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 lg:gap-3'
          }`}
        >
          <div className="flex items-center">
            <span className="text-sm font-medium text-text-primary">
              {selectedCount === 1
                ? '1 Vorgang ausgewählt'
                : `${selectedCount} Vorgänge ausgewählt`}
            </span>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
            {/* Clear selection */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onClearSelection}
                  disabled={disabled}
                  className="w-full sm:w-auto"
                >
                  <X className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Auswahl aufheben</p>
              </TooltipContent>
            </Tooltip>

            {/* Retry */}
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onBulkRetryClick}
                  disabled={selectedCount === 0 || disabled}
                  className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600 text-white hover:text-white shadow-sm"
                >
                  {isBulkRetryPending ? (
                    <RotateCcw className="h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="h-4 w-4" />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Vorgänge erneut verarbeiten</p>
              </TooltipContent>
            </Tooltip>

            {/* Delete */}
            <Tooltip>
              <TooltipTrigger asChild>
                <span className={hasErledigtItems ? 'cursor-not-allowed' : ''}>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={onBulkDeleteClick}
                    disabled={
                      selectedCount === 0 ||
                      hasLockedItems ||
                      hasErledigtItems ||
                      disabled
                    }
                    className="w-full sm:w-auto"
                  >
                    {isBulkDeletePending ? (
                      <Trash2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Trash2 className="h-4 w-4" />
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {hasErledigtItems
                    ? 'Erledigte Vorgänge können nicht gelöscht werden'
                    : 'Vorgänge löschen'}
                </p>
              </TooltipContent>
            </Tooltip>

            <Tooltip>
              <TooltipTrigger asChild>
                <span className={hasNonFehlerItems ? 'cursor-not-allowed' : ''}>
                  <Button
                    variant="shelve"
                    size="sm"
                    onClick={onBulkShelveClick}
                    disabled={
                      selectedCount === 0 || disabled || hasNonFehlerItems
                    }
                    className={`w-full sm:w-auto ${hasNonFehlerItems ? 'pointer-events-none' : ''}`}
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent>
                <p>
                  {hasNonFehlerItems
                    ? 'Nur Vorgänge im Status Fehler können zurückgestellt werden'
                    : 'Vorgänge zurückstellen'}
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>

        {hasLockedItems && (
          <div className="text-sm text-yellow-600 mt-3">
            Einige ausgewählte Vorgänge sind von anderen Benutzern gesperrt und
            können nicht gelöscht werden.
          </div>
        )}
      </div>
    </div>
  );
}
